import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ENV } from "../../utils/env.js";
import { useToaster } from "../../contexts/ToasterContext";
import { Card, CardContent } from "../../components/ui/card.jsx";
import { GitBranch } from "lucide-react";
import { Button } from "../../components/ui/button.jsx";
import { AlertCircle } from "lucide-react";
import { RefreshCw } from "lucide-react";
import { showFreeAssessmentToast } from "../../utils/freeAssessmentRedirect";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog.jsx";
import gitScanData from "../../rawData/gitscans.json";



// Error state component - shows backend-provided message
const ErrorState = ({ error, onRetry }) => (
  <Card>
    <CardContent className="text-center py-8">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <AlertCircle className="w-8 h-8 text-red-500" />
      </div>
      <p className="text-gray-900 font-semibold mb-4">{error}</p>
      <Button
        onClick={onRetry}
        className="bg-[#44087D] text-white hover:bg-[#5a0a9a]"
      >
        <RefreshCw className="w-4 h-4 mr-2" />
        Try Again
      </Button>
    </CardContent>
  </Card>
);

function RepositoryScheduleTable() {
  const { showToast } = useToaster();
  const navigate = useNavigate();
  const [repositories, setRepositories] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [mergedData, setMergedData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasNetworkError, setHasNetworkError] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);

  // Schedule form state
  const [repeatInterval, setRepeatInterval] = useState("1h");
  const [startTime, setStartTime] = useState("hour");
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [pagination, setPagination] = useState({
    has_next: false,
    has_previous: false,
    last_page: 0,
    next_page: 0,
    page: 1,
    per_page: 50,
    prev_page: 0,
    total_count: 0,
  });

  // Filter and search state
  const [filterOptions, setFilterOptions] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  // Helper functions for schedule management
  const formatProviderName = (providerString) => {
    // Normalize provider names: github -> Github, gitea -> Gitea, gitlab -> Gitlab
    if (!providerString) return "";
    const lower = providerString.toLowerCase().trim();

    const providerMap = {
      github: "Github",
      gitea: "Gitea",
      gitlab: "Gitlab",
      "github-enterprise": "Github Enterprise",
      "gitlab-self-hosted": "Gitlab Self-Hosted",
    };

    return (
      providerMap[lower] ||
      providerString.charAt(0).toUpperCase() +
        providerString.slice(1).toLowerCase()
    );
  };

  const getProviderLogo = (provider) => {
    if (!provider) return null;

    const providerLower = provider.toLowerCase().trim();
    console.log(
      "Provider value for logo:",
      provider,
      "Lowercased:",
      providerLower
    );

    const logoMap = {
      github: "/github.png",
      gitlab: "/gitlab.svg",
      bitbucket: "/bitbucket.png",
      gitea: "/inte/gitea.svg",
      "github-enterprise": "/inte/GHEnterprise.png",
      "gitlab-self-hosted": "/gitlab.svg",
    };

    const logoPath = logoMap[providerLower] || null;
    return logoPath;
  };

  const convertIntervalToMinutes = (interval) => {
    const match = interval.match(/^(\d+)([mhdw])$/);
    if (!match) return 60;

    const value = parseInt(match[1]);
    const unit = match[2];

    switch (unit) {
      case "m":
        return value;
      case "h":
        return value * 60;
      case "d":
        return value * 24 * 60;
      case "w":
        return value * 24 * 60 * 7;
      default:
        return 60;
    }
  };

  // Generate UTC start time
  const generateUTCStartTime = (startTimeOption) => {
    const now = new Date();

    if (startTimeOption === "now") {
      // Return current UTC time
      return now.toISOString();
    } else {
      // Start at the next hour in UTC
      const nextHour = new Date(now);
      nextHour.setUTCHours(now.getUTCHours() + 1, 0, 0, 0);
      return nextHour.toISOString();
    }
  };

  const getNextScheduleTime = (schedule) => {
    if (!schedule) return "N/A";

    // Use the nextScheduledAt from backend if available
    if (schedule.nextScheduledAt) {
      try {
        // Parse the ISO datetime string from backend
        const nextDate = new Date(schedule.nextScheduledAt);

        // Check if it's a valid date
        if (!isNaN(nextDate.getTime())) {
          return nextDate.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
      } catch (error) {
        console.error("Error parsing nextScheduledAt:", error);
      }
    }

    // Fallback: If nextScheduledAt is already formatted (from search), use it directly
    if (
      schedule.nextScheduledAt &&
      typeof schedule.nextScheduledAt === "string"
    ) {
      if (
        schedule.nextScheduledAt !== "N/A" &&
        schedule.nextScheduledAt.includes(",")
      ) {
        return schedule.nextScheduledAt;
      }
    }

    // Last resort fallback calculation
    const now = new Date();
    const startHour = schedule.startHour || 0;
    const repeatIntervalH = schedule.repeatIntervalH || 1;

    let nextScanTime = new Date(now);
    nextScanTime.setHours(startHour, 0, 0, 0);

    while (nextScanTime <= now) {
      nextScanTime.setTime(
        nextScanTime.getTime() + repeatIntervalH * 60 * 60 * 1000
      );
    }

    return nextScanTime.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Fetch filter options for scan schedules (from local mock data)
  const fetchFilterOptions = () => {
    const options = gitScanData.options || [];
    setFilterOptions(options);
  };

  // Fetch filtered results by provider (from local mock data)
  const fetchFilteredSchedules = async (
    provider,
    page = 1,
    perPage = 50,
    sortByParam = "name",
    sortOrderParam = "asc"
  ) => {
    try {
      console.log("=== FETCH FILTERED SCHEDULES (LOCAL) START ===");
      console.log("Provider:", provider);

      setLoading(true);

      const allData = gitScanData.data || [];
      const providerLower = provider?.toLowerCase();

      // Filter by provider if specified
      let filtered = allData;
      if (providerLower) {
        filtered = allData.filter(
          (item) =>
            (item.provider || "").toLowerCase() === providerLower
        );
      }

      // Apply basic pagination
      const total = filtered.length;
      const lastPage = Math.max(1, Math.ceil(total / perPage));
      const currentPage = Math.min(Math.max(page, 1), lastPage);
      const startIndex = (currentPage - 1) * perPage;
      const pageItems = filtered.slice(startIndex, startIndex + perPage);

      // Map to repository-like objects with embedded schedule data
      const enrichedRepos = pageItems.map((item) => ({
        id: item.id,
        RepositoryId: item.repositoryId,
        repository_name: item.repositoryName,
        owner: item.owner,
        provider: item.provider,
        Branch: item.branch || "main",
        StartTime: item.startTime,
        RepeatIntervalM: item.repeatIntervalM || 60,
        CreatedAt: item.createdAt,
        NextScheduledAt: item.nextScheduledAt,
        Active: item.active,
      }));

      setPagination({
        page: currentPage,
        perPage,
        total,
        lastPage,
        hasNext: currentPage < lastPage,
        hasPrevious: currentPage > 1,
        nextPage: currentPage < lastPage ? currentPage + 1 : null,
        prevPage: currentPage > 1 ? currentPage - 1 : null,
      });

      console.log("=== FETCH FILTERED SCHEDULES (LOCAL) COMPLETE ===");
      console.log("Enriched repos count:", enrichedRepos.length);

      // Clear schedules and set the filtered repositories
      setSchedules([]);
      setRepositories(enrichedRepos);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching filtered schedules (local):", error);
      setRepositories([]);
      setLoading(false);
    }
  };

  // Search scan schedules (local, from mock data)
  const searchSchedules = async (
    query,
    page = 1,
    perPage = 50,
    sortBy = "next_scheduled_at",
    sortOrder = "asc"
  ) => {
    try {
      const allData = gitScanData.data || [];
      const q = query.toLowerCase();

      const schedulesToDisplay = allData.filter((item) => {
        const name = (item.repositoryName || "").toLowerCase();
        const owner = (item.owner || "").toLowerCase();
        return name.includes(q) || owner.includes(q);
      });

      return schedulesToDisplay;
    } catch (error) {
      console.error("Error searching schedules (local):", error);
      return [];
    }
  };

  // Handle search - auto-triggers on query change
  const handleSearch = useCallback(async (query, provider = null) => {
    if (!query.trim()) {
      setSearchResults(null);
      return;
    }

    setIsSearching(true);
    try {
      // Fetch search results from schedules API
      const results = await searchSchedules(
        query,
        1,
        50,
        "next_scheduled_at",
        "asc"
      );

      console.log("Raw search results from API:", results);
      console.log("Results length:", results.length);

      if (!results || results.length === 0) {
        console.warn("No search results returned from API");
        setSearchResults([]);
        setMergedData([]);
        return;
      }

      // Fetch all repositories to get owner information
      let repositoriesMap = {};
      try {
        const repoResponse = await axios.get(
          `${ENV.REACT_APP_URL}api/repository?page=1&per_page=1000`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("jwt")}`,
            },
          }
        );

        // Create a map of repository ID to repository data for quick lookup
        if (repoResponse.data.data && Array.isArray(repoResponse.data.data)) {
          repoResponse.data.data.forEach((repo) => {
            repositoriesMap[repo.id] = repo;
            // Also map by repositoryId if available
            if (repo.repositoryId) {
              repositoriesMap[repo.repositoryId] = repo;
            }
          });
        }
        console.log("Repository map created:", repositoriesMap);
      } catch (repoError) {
        console.error("Error fetching repositories for owner info:", repoError);
        // Continue without owner info if fetch fails
      }

      // Transform results to repository format matching the table expectations
      const transformedRepos = results.map((item, index) => {
        console.log(`Processing search result ${index}:`, item);

        // Extract repository name with multiple fallbacks
        const repoName =
          item.repository_name ||
          item.RepositoryName ||
          item.repositoryName ||
          item.name ||
          `Repository ${item.id || index}`;
        const repoId =
          item.RepositoryId ||
          item.repositoryId ||
          item.id ||
          `search-${index}`;
        const provider =
          item.provider ||
          item.repository_provider ||
          item.Provider ||
          "github";
        const branch = item.Branch || item.branch || item.BranchName || "main";

        // Try to get owner from repository map first
        let owner = "Unknown";
        const repoData = repositoriesMap[repoId];
        if (repoData && repoData.owner) {
          owner = repoData.owner;
        } else if (item.owner || item.Owner) {
          // Fallback to data from search results
          owner = item.owner || item.Owner;
        }

        // Helper function to format date
        const formatDate = (dateString) => {
          if (!dateString) return "N/A";
          try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "N/A";
            return date.toLocaleString("en-US", {
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            });
          } catch (e) {
            return "N/A";
          }
        };

        const scheduleData = {
          id: item.Id || item.id,
          repositoryId: repoId,
          repositoryName: repoName,
          branch: branch,
          startHour: item.StartHour || item.startHour || item.StartTime || 0,
          repeatIntervalH: item.RepeatIntervalM
            ? item.RepeatIntervalM / 60
            : item.repeatIntervalH || 1,
          timezone: item.Timezone || item.timezone || "UTC +00:00",
          repeatInterval: `${
            item.RepeatIntervalM
              ? item.RepeatIntervalM / 60
              : item.repeatIntervalH || 1
          }h`,
          startTime:
            (item.StartHour || item.startHour || 0) === 0 ? "now" : "hour",
          createdAt:
            item.CreatedAt || item.createdAt || new Date().toISOString(),
          nextScheduledAt: formatDate(
            item.NextScheduledAt || item.next_scheduled_at
          ),
          active: item.Active || item.active || false,
        };

        return {
          id: repoId,
          name: repoName,
          owner: owner,
          provider: provider,
          type: item.type || "repository",
          integration: item.integration,
          branch: branch,
          schedule: scheduleData,
          hasSchedule: true,
          rowId: `search-${repoId}-${branch}`,
        };
      });

      // If a provider is selected, filter search results to that provider
      const filteredResults = provider
        ? transformedRepos.filter(
            (repo) =>
              (repo.provider || "").toLowerCase() === provider.toLowerCase()
          )
        : transformedRepos;

      setSearchResults(filteredResults);
      // Update mergedData to show search results in the normal table
      setMergedData(filteredResults);
      console.log("Transformed search results:", filteredResults);
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
      setMergedData([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Auto-trigger search when query changes
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchQuery.trim()) {
        handleSearch(searchQuery, selectedProvider);
      } else {
        // Clear search results when query is empty
        setSearchResults(null);
        // Rebuild mergedData from current repositories and schedules (non-search data)
        const merged = [];
        repositories.forEach((repo) => {
          // Check if repository has embedded schedule data (from filter-options endpoint)
          const hasIntervalField =
            repo.StartTime ||
            repo.repeatIntervalM ||
            repo.RepeatIntervalM ||
            repo.startTime;
          const hasRepoId = repo.Id || repo.id;
          const hasEmbeddedSchedule = hasRepoId && hasIntervalField;

          if (hasEmbeddedSchedule) {
            // Repository has embedded schedule data - create schedule object from it
            const repeatIntervalM =
              repo.RepeatIntervalM || repo.repeatIntervalM || 60;

            const scheduleObj = {
              id: repo.Id || repo.id,
              repositoryId: repo.RepositoryId || repo.id,
              repositoryName: repo.repository_name || repo.name,
              branch: repo.Branch || repo.branch || "main",
              startHour: repo.StartTime
                ? new Date(repo.StartTime).getUTCHours()
                : 0,
              repeatIntervalH: repeatIntervalM / 60,
              timezone: "UTC +00:00",
              repeatInterval: `${repeatIntervalM / 60}h`,
              startTime: "hour",
              createdAt: repo.CreatedAt || new Date().toISOString(),
              nextScheduledAt:
                repo.NextScheduledAt ||
                repo.nextScheduledAt ||
                repo.next_scheduled_at,
              active: repo.Active || repo.active || false,
            };

            merged.push({
              id: repo.RepositoryId || repo.id,
              name: repo.repository_name || repo.name,
              owner: repo.owner,
              provider: repo.provider || repo.repository_provider || "unknown",
              type: repo.type,
              integration: repo.integration,
              branch: scheduleObj.branch,
              schedule: scheduleObj,
              hasSchedule: true,
              rowId: `${repo.RepositoryId || repo.id}-${scheduleObj.branch}`,
            });
          } else {
            // Repository doesn't have embedded schedule - look for separate schedule objects
            const repoSchedules = schedules.filter((s) => {
              const scheduleRepoId = s.repositoryId || s.RepositoryId;
              const repoId = repo.id;

              if (!scheduleRepoId || !repoId) return false;

              const matches =
                scheduleRepoId === repoId ||
                scheduleRepoId === repoId.toString() ||
                scheduleRepoId.toString() === repoId ||
                scheduleRepoId.toString() === repoId.toString();

              return matches;
            });

            if (repoSchedules.length > 0) {
              repoSchedules.forEach((schedule) => {
                merged.push({
                  ...repo,
                  schedule: schedule,
                  hasSchedule: true,
                  branch: schedule.branch || repo.branch || "main",
                  provider: repo.provider || "unknown",
                  rowId: `${repo.id}-${schedule.branch || "main"}`,
                });
              });
            } else {
              merged.push({
                ...repo,
                schedule: null,
                hasSchedule: false,
                branch: repo.branch || "main",
                provider: repo.provider || "unknown",
                rowId: `${repo.id}-${repo.branch || "main"}`,
              });
            }
          }
        });
        setMergedData(merged);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line
  }, [searchQuery, handleSearch, selectedProvider, repositories, schedules]);

  // Handle clear search
  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
    // SearchResults will be set to null by the useEffect above
    // and mergedData will be restored with normal repositories
    // eslint-disable-next-line
  }, []);

  const fetchSchedules = async (
    page = 1,
    per_page = 50,
    sortByParam = "next_scheduled_at",
    sortOrderParam = "asc"
  ) => {
    try {
      // Use new API endpoint with pagination and sort parameters
      const base =
        ENV.REACT_APP_URL || (typeof window !== "undefined"
          ? window.location.origin + "/"
          : "http://localhost/");
      const url = new URL("api/scan-schedules", base);
      url.searchParams.append("page", page.toString());
      url.searchParams.append("per_page", per_page.toString());
      url.searchParams.append("sort_by", sortByParam);
      url.searchParams.append("sort_order", sortOrderParam);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          setSchedules([]);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      console.log("=== FETCH SCHEDULES STANDALONE DEBUG ===");
      console.log("API URL:", url.toString());
      console.log("API Response:", data);
      console.log("Pagination in response:", data.pagination);

      let schedulesToDisplay = [];

      // Handle different response formats
      if (data && data.response && Array.isArray(data.response)) {
        schedulesToDisplay = data.response;
      } else if (data && data.data && Array.isArray(data.data)) {
        schedulesToDisplay = data.data;
      } else if (data && data.schedules && Array.isArray(data.schedules)) {
        schedulesToDisplay = data.schedules;
      } else if (Array.isArray(data)) {
        schedulesToDisplay = data;
      }

      const mappedSchedules = schedulesToDisplay.map((schedule, index) => {
        // Handle both minute and hour interval formats
        const repeatIntervalM =
          schedule.repeatIntervalM || schedule.RepeatIntervalM;
        const repeatIntervalH =
          schedule.repeatIntervalH || schedule.RepeatIntervalH;

        let finalIntervalH;
        let repeatInterval;

        if (repeatIntervalM) {
          // Convert minutes to hours
          finalIntervalH = repeatIntervalM / 60;
          if (repeatIntervalM < 60) {
            repeatInterval = `${repeatIntervalM}m`;
          } else if (repeatIntervalM % 60 === 0) {
            repeatInterval = `${repeatIntervalM / 60}h`;
          } else {
            repeatInterval = `${repeatIntervalM}m`;
          }
        } else if (repeatIntervalH) {
          finalIntervalH = repeatIntervalH;
          if (finalIntervalH < 1) {
            const minutes = Math.round(finalIntervalH * 60);
            repeatInterval = `${minutes}m`;
          } else if (finalIntervalH === Math.floor(finalIntervalH)) {
            repeatInterval = `${finalIntervalH}h`;
          } else {
            repeatInterval = `${finalIntervalH}h`;
          }
        } else {
          finalIntervalH = 1;
          repeatInterval = "1h";
        }

        const mappedSchedule = {
          id: schedule.id || schedule.Id || `fetched-${Date.now()}-${index}`,
          repositoryId:
            schedule.repositoryId ||
            schedule.repositoryID ||
            schedule.repoId ||
            schedule.RepoId,
          repositoryName:
            schedule.repositoryName ||
            schedule.repoName ||
            schedule.RepoName ||
            `Repository ${
              schedule.repositoryId ||
              schedule.repositoryID ||
              schedule.repoId ||
              schedule.RepoId
            }`,
          branch: schedule.branch || schedule.Branch || "main",
          owner: schedule.owner || schedule.Owner || "Unknown",
          provider: schedule.provider || schedule.Provider || "Unknown",
          startHour: schedule.startHour || schedule.StartHour || 0,
          repeatIntervalH: finalIntervalH,
          timezone: schedule.timezone || schedule.Timezone || "UTC +00:00",
          repeatInterval: repeatInterval,
          startTime:
            (schedule.startHour || schedule.StartHour) === 0 ? "now" : "hour",
          createdAt: schedule.createdAt
            ? new Date(schedule.createdAt).toISOString()
            : new Date().toISOString(),
          nextScheduledAt:
            schedule.nextScheduledAt ||
            schedule.NextScheduledAt ||
            schedule.next_scheduled_at,
          active: schedule.active || schedule.Active || false,
        };

        return mappedSchedule;
      });

      setSchedules(mappedSchedules);

      // Update pagination state if the response includes pagination data
      if (data && data.pagination) {
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error("Error fetching schedules:", error);
      setSchedules([]);
    }
  };

  const fetchRepos = async (page = 1, per_page = 50) => {
    try {
      setLoading(true);
      setError(null);

      const allData = gitScanData.data || [];

      // Map schedules from local JSON
      const mappedSchedules = allData.map((item) => {
        const repeatIntervalM = item.repeatIntervalM || 60;
        const repeatIntervalH = repeatIntervalM / 60;
        const repeatInterval =
          repeatIntervalM < 60 ? `${repeatIntervalM}m` : `${repeatIntervalH}h`;

        return {
          id: item.id,
          repositoryId: item.repositoryId,
          repositoryName: item.repositoryName,
          branch: item.branch || "main",
          owner: item.owner || "Unknown",
          provider: item.provider || "Unknown",
          startHour: item.startTime
            ? new Date(item.startTime).getUTCHours()
            : 0,
          repeatIntervalH,
          timezone: "UTC +00:00",
          repeatInterval,
          startTime: "hour",
          createdAt: item.createdAt || new Date().toISOString(),
          nextScheduledAt: item.nextScheduledAt,
          active: item.active !== false,
          lastScheduledAt: item.lastScheduledAt,
          lastDoneAt: item.lastDoneAt,
        };
      });

      setSchedules(mappedSchedules);

      const uniqueReposMap = new Map();

      mappedSchedules.forEach((schedule) => {
        if (
          schedule.repositoryId &&
          !uniqueReposMap.has(schedule.repositoryId)
        ) {
          uniqueReposMap.set(schedule.repositoryId, {
            id: schedule.repositoryId,
            name: schedule.repositoryName,
            owner: schedule.owner,
            provider: schedule.provider,
            type: "repository",
            integration: null,
            branch: "main",
            schedule: schedule,
            hasSchedule: true,
            rowId: `${schedule.repositoryId}-main`,
          });
        }
      });

      const repoLikeData = Array.from(uniqueReposMap.values());
      setRepositories(repoLikeData);

      const paginationData = gitScanData.pagination || {
        has_next: false,
        has_previous: false,
        last_page: 1,
        next_page: 0,
        page: 1,
        per_page: per_page,
        prev_page: 0,
        total_count: mappedSchedules.length,
      };

      console.log("Setting pagination (local):", paginationData);
      setPagination(paginationData);
      setHasNetworkError(false);
    } catch (error) {
      console.error("Error in fetchRepos (local):", error);
      setError("Failed to load repository scan schedules");
    } finally {
      setLoading(false);
    }
  };

  // Merge repositories with schedule data - ALWAYS merge all data the same way
  useEffect(() => {
    console.log("=== MERGE EFFECT TRIGGERED ===");
    console.log("selectedProvider:", selectedProvider);
    console.log("repositories:", repositories);
    console.log("schedules:", schedules);

    const merged = [];
    const matchedScheduleIds = new Set(); // Track which schedules have been matched

    // First pass: merge repositories with their schedules
    repositories.forEach((repo) => {
      // When filtering by provider, repositories come from filter-options endpoint with embedded schedule data
      // When in "All" view, they come from fetchRepos which separates repos and schedules
      // Check if repo has embedded schedule data (from filter-options endpoint)
      const hasIntervalField =
        repo.StartTime ||
        repo.repeatIntervalM ||
        repo.RepeatIntervalM ||
        repo.startTime;
      const hasRepoId = repo.Id || repo.id;
      const hasEmbeddedSchedule = hasRepoId && hasIntervalField;

      console.log("Processing repo:", {
        name: repo.name || repo.repository_name,
        hasEmbeddedSchedule,
        hasRepoId,
        hasIntervalField,
        Id: repo.Id,
        id: repo.id,
        Branch: repo.Branch,
        branch: repo.branch,
        StartTime: repo.StartTime,
        RepeatIntervalM: repo.RepeatIntervalM,
        selectedProvider,
      });

      if (hasEmbeddedSchedule) {
        // Repository has embedded schedule data - create schedule object from it
        const repeatIntervalM =
          repo.RepeatIntervalM || repo.repeatIntervalM || 60;

        const scheduleObj = {
          id: repo.Id || repo.id,
          repositoryId: repo.RepositoryId || repo.id,
          repositoryName: repo.repository_name || repo.name,
          branch: repo.Branch || repo.branch || "main",
          startHour: repo.StartTime
            ? new Date(repo.StartTime).getUTCHours()
            : 0,
          repeatIntervalH: repeatIntervalM / 60,
          timezone: "UTC +00:00",
          repeatInterval: `${repeatIntervalM / 60}h`,
          startTime: "hour",
          createdAt: repo.CreatedAt || new Date().toISOString(),
        };

        console.log("Created schedule object:", scheduleObj);

        merged.push({
          // Normalize to lowercase fields for consistency
          id: repo.RepositoryId || repo.id,
          name: repo.repository_name || repo.name,
          owner: repo.owner,
          provider: repo.provider || repo.repository_provider || "unknown",
          type: repo.type,
          integration: repo.integration,
          branch: scheduleObj.branch,
          schedule: scheduleObj,
          hasSchedule: true,
          rowId: `${repo.RepositoryId || repo.id}-${scheduleObj.branch}`,
        });
        matchedScheduleIds.add(scheduleObj.id);
      } else {
        // Repository doesn't have embedded schedule - look for separate schedule objects
        const repoSchedules = schedules.filter((s) => {
          const scheduleRepoId = s.repositoryId || s.RepositoryId;
          const repoId = repo.id;

          // Safely handle undefined values
          if (!scheduleRepoId || !repoId) return false;

          // Try multiple comparison methods to handle different data types
          const matches =
            scheduleRepoId === repoId ||
            scheduleRepoId === repoId.toString() ||
            scheduleRepoId.toString() === repoId ||
            scheduleRepoId.toString() === repoId.toString();

          return matches;
        });

        if (repoSchedules.length > 0) {
          // Create a row for each schedule (each branch)
          repoSchedules.forEach((schedule) => {
            merged.push({
              ...repo,
              schedule: schedule,
              hasSchedule: true,
              branch: schedule.branch || repo.branch || "main",
              provider: repo.provider || "unknown",
              rowId: `${repo.id}-${schedule.branch || "main"}`,
            });
            matchedScheduleIds.add(schedule.id);
          });
        } else {
          // No schedule for this repo, add it once with default branch
          merged.push({
            ...repo,
            schedule: null,
            hasSchedule: false,
            branch: repo.branch || "main",
            provider: repo.provider || "unknown",
            rowId: `${repo.id}-${repo.branch || "main"}`,
          });
        }
      }
    });

    // Second pass: add any schedules that weren't matched to a repository
    schedules.forEach((schedule) => {
      if (!matchedScheduleIds.has(schedule.id)) {
        console.log("Found unmatched schedule:", schedule);
        // This schedule has no matching repository - add it anyway
        merged.push({
          id: schedule.repositoryId,
          name:
            schedule.repositoryName || `Repository ${schedule.repositoryId}`,
          owner: "Unknown",
          provider: "unknown",
          type: null,
          integration: null,
          branch: schedule.branch || "main",
          schedule: schedule,
          hasSchedule: true,
          rowId: `${schedule.repositoryId}-${schedule.branch || "main"}`,
        });
      }
    });

    console.log("=== MERGED DATA FINAL ===");
    console.log("Total merged items:", merged.length);
    console.log("Matched schedules:", matchedScheduleIds.size);
    console.log("Total schedules:", schedules.length);
    console.log("Merged data:", merged);

    setMergedData(merged);
  }, [repositories, schedules, selectedProvider]);

  useEffect(() => {
    fetchRepos();
    fetchFilterOptions();
    // eslint-disable-next-line
  }, []);

  // Schedule CRUD operations
  const createSchedule = async (scheduleData) => {
    // Mock create: no API calls in this demo view
    console.warn("createSchedule called in mock mode; no API request sent.", scheduleData);
    return { statusCode: 201, Id: "mock-id", ...scheduleData };
  };

  const updateSchedule = async (scheduleId, scheduleData) => {
    // Mock update: no API calls in this demo view
    console.warn("updateSchedule called in mock mode; no API request sent.", {
      scheduleId,
      scheduleData,
    });
    return { id: scheduleId, ...scheduleData };
  };

  const deleteSchedule = async (scheduleId) => {
    // Mock delete: no API calls in this demo view
    console.warn("deleteSchedule called in mock mode; no API request sent.", {
      scheduleId,
    });
    return { success: true };
  };

  // Handle schedule actions
  const handleCreateSchedule = async () => {
    if (!selectedRepo) return;

    try {
      setScheduleLoading(true);
      const scheduleData = {
        startTime: generateUTCStartTime(startTime),
        repeatIntervalM: convertIntervalToMinutes(repeatInterval),
        repositoryId: selectedRepo.id,
        branch: "main",
      };

      const result = await createSchedule(scheduleData);

      if (result.Id || result.id) {
        showToast("Schedule created successfully!", "success");
        setShowScheduleModal(false);
        setSelectedRepo(null);
        await fetchSchedules(pagination.page, pagination.per_page);
      }
    } catch (err) {
      const errorMessage =
        err?.message || "Failed to create schedule. Please try again.";
      showToast(errorMessage, "error");
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleUpdateSchedule = async () => {
    if (!editingSchedule) return;

    try {
      setScheduleLoading(true);
      const updateData = {
        startTime: generateUTCStartTime(startTime),
        repeatIntervalM: convertIntervalToMinutes(repeatInterval),
        repositoryId: editingSchedule.repositoryId,
        branch: editingSchedule.branch || "main",
        active:
          editingSchedule.active !== undefined ? editingSchedule.active : true,
      };

      await updateSchedule(editingSchedule.id, updateData);
      showToast("Schedule updated successfully!", "success");
      setShowScheduleModal(false);
      setEditingSchedule(null);

      // Refresh data based on current view
      if (selectedProvider) {
        // If a provider filter is active, refresh the filtered view
        await fetchFilteredSchedules(
          selectedProvider,
          pagination.page || 1,
          pagination.per_page || 50,
          "name",
          "asc"
        );
      } else {
        // Otherwise refresh all schedules
        await fetchSchedules(pagination.page, pagination.per_page);
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to update schedule";
      showToast(errorMessage, "error");
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleDeleteSchedule = (schedule) => {
    if (!schedule?.id) return;
    setScheduleToDelete(schedule);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteSchedule = async () => {
    if (!scheduleToDelete?.id) return;

    try {
      await deleteSchedule(scheduleToDelete.id);
      showToast("Schedule deleted successfully!", "success");
      await fetchSchedules(pagination.page, pagination.per_page);
    } catch (error) {
      const errorMessage = error?.message || "Failed to delete schedule";
      showToast(errorMessage, "error");
    } finally {
      setDeleteDialogOpen(false);
      setScheduleToDelete(null);
    }
  };

  // Modal handlers
  const openScheduleModal = (repo, schedule = null) => {
    setSelectedRepo(repo);
    setEditingSchedule(schedule);

    if (schedule) {
      // Editing existing schedule
      setRepeatInterval(schedule.repeatInterval || "1h");
      setStartTime(schedule.startTime || "hour");
    } else {
      // Creating new schedule
      setRepeatInterval("1h");
      setStartTime("hour");
    }

    setShowScheduleModal(true);
  };

  const closeScheduleModal = () => {
    setShowScheduleModal(false);
    setSelectedRepo(null);
    setEditingSchedule(null);
  };

  // Pagination controls
  const handlePrevPage = () => {
    if (pagination.has_previous && !hasNetworkError) {
      if (selectedProvider) {
        fetchFilteredSchedules(
          selectedProvider,
          pagination.prev_page,
          pagination.per_page,
          "name",
          "asc"
        );
      } else {
        // Call both fetchRepos and fetchSchedules to keep data in sync
        fetchRepos(pagination.prev_page, pagination.per_page);
      }
    }
  };

  const handleNextPage = () => {
    if (pagination.has_next && !hasNetworkError) {
      if (selectedProvider) {
        fetchFilteredSchedules(
          selectedProvider,
          pagination.next_page,
          pagination.per_page,
          "name",
          "asc"
        );
      } else {
        // Call both fetchRepos and fetchSchedules to keep data in sync
        fetchRepos(pagination.next_page, pagination.per_page);
      }
    }
  };

  const handlePerPageChange = (perPage) => {
    if (!hasNetworkError) {
      // Update pagination state immediately for UI responsiveness
      setPagination((prev) => ({
        ...prev,
        per_page: perPage,
        page: 1,
      }));

      if (selectedProvider) {
        fetchFilteredSchedules(selectedProvider, 1, perPage, "name", "asc");
      } else {
        fetchRepos(1, perPage);
      }
    }
  };

  const handleRetry = () => {
    setHasNetworkError(false);
    setError(null);
    fetchRepos();
  };

  // Function to generate pagination buttons
  const generatePaginationButtons = () => {
    const buttons = [];
    const { page, last_page } = pagination;

    // Always show the first page
    buttons.push(
      <button
        key={1}
        onClick={() => !hasNetworkError && fetchRepos(1, pagination.per_page)}
        disabled={hasNetworkError}
        className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
          page === 1
            ? "bg-[#44087D] text-white"
            : "bg-[#F4F0FD] text-[#8D82AA] hover:bg-[#44087D] hover:text-white"
        } ${hasNetworkError ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        1
      </button>
    );

    // Show ellipsis if current page is far from the first page
    if (page > 3) {
      buttons.push(
        <span key="ellipsis-start" className="px-3 py-2 text-sm text-gray-600">
          ...
        </span>
      );
    }

    // Show pages around the current page
    for (
      let i = Math.max(2, page - 1);
      i <= Math.min(last_page - 1, page + 1);
      i++
    ) {
      buttons.push(
        <button
          key={i}
          onClick={() => !hasNetworkError && fetchRepos(i, pagination.per_page)}
          disabled={hasNetworkError}
          className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
            page === i
              ? "bg-[#44087D] text-white"
              : "bg-[#F4F0FD] text-[#8D82AA] hover:bg-[#44087D] hover:text-white"
          } ${hasNetworkError ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {i}
        </button>
      );
    }

    // Show ellipsis if current page is far from the last page
    if (page < last_page - 2) {
      buttons.push(
        <span key="ellipsis-end" className="px-3 py-2 text-sm text-gray-600">
          ...
        </span>
      );
    }

    // Always show the last page
    if (last_page > 1) {
      buttons.push(
        <button
          key={last_page}
          onClick={() =>
            !hasNetworkError && fetchRepos(last_page, pagination.per_page)
          }
          disabled={hasNetworkError}
          className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
            page === last_page
              ? "bg-[#44087D] text-white"
              : "bg-[#F4F0FD] text-[#8D82AA] hover:bg-[#44087D] hover:text-white"
          } ${hasNetworkError ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {last_page}
        </button>
      );
    }

    return buttons;
  };

  if (error) {
    return <ErrorState error={error} onRetry={handleRetry} />;
  }

  // Calculate total count from all filter options
  const totalCount = filterOptions.reduce(
    (sum, option) => sum + option.count,
    0
  );

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="flex-none">
        {/* Action Bar / Search */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div className="relative flex-1 w-full md:max-w-md">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search scan schedules by repository name..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#44087D] focus:border-transparent"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
            {isSearching && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-[#44087D] border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>

          <Button
            onClick={() => {
              showFreeAssessmentToast();
            }}
            className="bg-[#44087D] text-white hover:bg-[#5a0a9a] whitespace-nowrap"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            New Scan
          </Button>
        </div>

        {/* Filter Badges - ALWAYS VISIBLE, independent of search */}
        <div className="mb-6">
          {/* Filter Badges */}
          <div className="flex flex-wrap gap-2 mb-4">
            {/* All Badge */}
            <button
              onClick={() => {
                setSelectedProvider(null);
                fetchRepos(1, pagination.per_page);
              }}
              className={`inline-flex items-center px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                selectedProvider === null
                  ? "bg-[#44087D] text-white"
                  : "bg-[#F4F0FD] text-[#8D82AA] hover:bg-[#44087D] hover:text-white"
              }`}
            >
              All
              <span
                className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  selectedProvider === null
                    ? "bg-white/20 text-white"
                    : "bg-[#E0D9F0] text-[#5D5275]"
                }`}
              >
                {totalCount}
              </span>
            </button>

            {/* Provider Badges */}
            {filterOptions.map((option) => {
              const logo = getProviderLogo(option.provider);
              return (
                <button
                  key={option.provider}
                  onClick={() => {
                    console.log(
                      "=== SWITCHING PROVIDER TO:",
                      option.provider,
                      "==="
                    );
                    setSelectedProvider(option.provider);
                    setSearchResults(null); // Clear search results when switching providers
                    setRepositories([]); // Clear old repositories BEFORE fetching new ones
                    setSchedules([]); // Clear old schedules
                    fetchFilteredSchedules(
                      option.provider,
                      1,
                      pagination.per_page,
                      "name",
                      "asc"
                    );
                  }}
                  title={formatProviderName(option.provider)}
                  className={`inline-flex items-center px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 capitalize ${
                    selectedProvider === option.provider
                      ? "bg-[#44087D] text-white"
                      : "bg-[#F4F0FD] text-[#8D82AA] hover:bg-[#44087D] hover:text-white"
                  }`}
                >
                  {logo && (
                    <img
                      src={logo}
                      alt={option.provider}
                      className="w-5 h-5 mr-2 object-contain"
                    />
                  )}
                  <span className="mr-1">
                    {formatProviderName(option.provider)}
                  </span>
                  <span
                    className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                      selectedProvider === option.provider
                        ? "bg-white/20 text-white"
                        : "bg-[#E0D9F0] text-[#5D5275]"
                    }`}
                  >
                    {option.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Table - renders search results or normal data based on searchResults state */}
      <div className="flex-1 min-h-0 border border-gray-300 rounded-xl overflow-hidden relative">
        <div className="absolute inset-0 overflow-auto scrollbar-thin scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400">
          <table className="min-w-full relative">
            <thead className="sticky top-0 z-10">
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="p-3 text-left text-sm font-semibold text-gray-800">
                  Repository
                </th>
                <th className="p-3 text-left text-sm font-semibold text-gray-800">
                  Branch
                </th>
                <th className="p-3 text-left text-sm font-semibold text-gray-800">
                  Owner
                </th>
                {/* Provider column removed */}
                <th className="p-3 text-left text-sm font-semibold text-gray-800">
                  Schedule Status
                </th>
                <th className="p-3 text-left text-sm font-semibold text-gray-800">
                  Current Interval
                </th>
                <th className="p-3 text-left text-sm font-semibold text-gray-800">
                  Next Scan
                </th>
                <th className="p-3 text-left text-sm font-semibold text-gray-800">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading
                ? Array.from({ length: 10 }).map((_, index) => (
                    <tr key={index} className="animate-pulse">
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center">
                            <img
                              src="/gitrepo.svg"
                              alt="Repository"
                              className="w-4 h-4 opacity-30"
                              style={{ filter: "brightness(0) invert(1)" }}
                            />
                          </div>
                          <div className="w-32 h-4 bg-gray-200 rounded"></div>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="w-20 h-4 bg-gray-200 rounded"></div>
                      </td>
                      <td className="p-3">
                        <div className="w-24 h-4 bg-gray-200 rounded"></div>
                      </td>
                      <td className="p-3">
                        <div className="w-20 h-6 bg-gray-200 rounded-full"></div>
                      </td>
                      <td className="p-3">
                        <div className="w-16 h-4 bg-gray-200 rounded"></div>
                      </td>
                      <td className="p-3">
                        <div className="w-24 h-4 bg-gray-200 rounded"></div>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <div className="w-16 h-6 bg-gray-200 rounded"></div>
                          <div className="w-16 h-6 bg-gray-200 rounded"></div>
                        </div>
                      </td>
                    </tr>
                  ))
                : (() => {
                    const filtered = mergedData.filter((repo) => {
                      // If searching, show search results
                      if (searchResults !== null) {
                        return searchResults.some(
                          (sr) =>
                            sr.id === repo.id || sr.RepositoryId === repo.id
                        );
                      }
                      // If provider filter active, filter by that provider
                      if (selectedProvider) {
                        const repoProvider = (
                          repo.provider || ""
                        ).toLowerCase();
                        console.log("Filtering repo:", {
                          name: repo.name,
                          repoProvider,
                          selectedProvider: selectedProvider.toLowerCase(),
                          matches:
                            repoProvider === selectedProvider.toLowerCase(),
                        });
                        return repoProvider === selectedProvider.toLowerCase();
                      }
                      // Otherwise show all
                      return true;
                    });

                    console.log("=== TABLE RENDER ===");
                    console.log("mergedData count:", mergedData.length);
                    console.log("selectedProvider:", selectedProvider);
                    console.log("filtered count:", filtered.length);
                    console.log("filtered data:", filtered);

                    return filtered.map((repo) => (
                      <tr
                        key={repo.rowId || repo.id}
                        className="hover:bg-gray-50 transition-colors duration-200"
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            {/* Provider icon without background */}
                            <div className="w-8 h-8 flex items-center justify-center">
                              {(() => {
                                const providerValue =
                                  repo.provider ||
                                  repo.type ||
                                  repo.integration;
                                const logoPath = getProviderLogo(providerValue);
                                return logoPath ? (
                                  <img
                                    src={logoPath}
                                    alt={
                                      formatProviderName(providerValue) ||
                                      "Unknown"
                                    }
                                    className="h-5 w-5 max-w-[20px]"
                                    title={
                                      formatProviderName(providerValue) ||
                                      "Unknown"
                                    }
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <span className="text-gray-400 font-bold text-lg">
                                    ?
                                  </span>
                                );
                              })()}
                            </div>
                            <span className="font-medium text-gray-900 truncate max-w-[150px] sm:max-w-[200px] md:max-w-[250px]">
                              {repo.name}
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <GitBranch className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-600 truncate max-w-[120px] sm:max-w-[180px] md:max-w-[200px]">
                              {repo.branch || "main"}
                            </span>
                          </div>
                        </td>
                        <td className="p-3 text-sm text-gray-600 truncate max-w-[150px] sm:max-w-[200px] md:max-w-none">
                          {repo.owner}
                        </td>
                        {/* Provider column removed */}
                        <td className="p-3">
                          {repo.hasSchedule ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <svg
                                className="w-3 h-3 mr-1"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Scheduled
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              <svg
                                className="w-3 h-3 mr-1"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Not Scheduled
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-sm text-gray-600">
                          {repo.hasSchedule ? (
                            <span className="font-medium">
                              Every {repo.schedule.repeatInterval}
                              {repo.schedule.repeatInterval &&
                                repo.schedule.repeatInterval.endsWith("m") && (
                                  <span className="text-gray-500 ml-1">
                                    (
                                    {(
                                      convertIntervalToMinutes(
                                        repo.schedule.repeatInterval
                                      ) / 60
                                    ).toFixed(2)}
                                    h)
                                  </span>
                                )}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="p-3 text-sm text-gray-600">
                          {repo.hasSchedule ? (
                            <span className="font-medium">
                              {getNextScheduleTime(repo.schedule)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {repo.hasSchedule ? (
                              <>
                                <button
                                  onClick={() =>
                                    openScheduleModal(repo, repo.schedule)
                                  }
                                  className="p-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
                                  title="Edit schedule"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                    />
                                  </svg>
                                </button>
                                <button
                                  onClick={() =>
                                    handleDeleteSchedule(repo.schedule)
                                  }
                                  className="p-2 text-gray-600 hover:text-red-600 transition-colors duration-200"
                                  title="Delete schedule"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                  </svg>
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => {
                                  showFreeAssessmentToast();
                                }}
                                className="px-3 py-1.5 text-sm bg-[#44087D] text-white rounded-lg hover:bg-[#5a0a94] transition-all duration-200 font-medium shadow-sm hover:shadow-md flex items-center gap-1"
                              >
                                <svg
                                  className="w-3 h-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                  />
                                </svg>
                                Schedule
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ));
                  })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      <div className="flex-none flex flex-col sm:flex-row items-center justify-center my-6 gap-4">
        <div className="flex items-center gap-2">
          <Button
            onClick={handlePrevPage}
            disabled={!pagination.has_previous || hasNetworkError}
            className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
              !pagination.has_previous || hasNetworkError
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-[#44087D] text-white hover:bg-[#5a0a94]"
            }`}
          >
            Previous
          </Button>
          {generatePaginationButtons()}
          <Button
            onClick={handleNextPage}
            disabled={!pagination.has_next || hasNetworkError}
            className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
              !pagination.has_next || hasNetworkError
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-[#44087D] text-white hover:bg-[#5a0a94]"
            }`}
          >
            Next
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Per page:</label>
          <select
            value={pagination.per_page}
            onChange={(e) => handlePerPageChange(Number(e.target.value))}
            disabled={hasNetworkError}
            className={`px-3 py-2 rounded text-sm bg-[#F4F0FD] text-[#8D82AA] border border-[#44087D] focus:outline-none ${
              hasNetworkError ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {[10, 20, 30, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Schedule Configuration Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 relative">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {editingSchedule ? "Edit Schedule" : "Create Schedule"}
              </h3>
              <button
                onClick={closeScheduleModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {selectedRepo && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-[#44087D] rounded flex items-center justify-center">
                    <img
                      src="/gitrepo.svg"
                      alt="Repository"
                      className="w-3 h-3 text-white"
                      style={{ filter: "brightness(0) invert(1)" }}
                    />
                  </div>
                  <span className="font-medium text-gray-900">
                    {selectedRepo.name}
                  </span>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Time
                </label>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="startTime"
                      value="now"
                      checked={startTime === "now"}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="w-4 h-4 text-[#6D49FE] bg-gray-100 border-gray-300 focus:ring-[#6D49FE] focus:ring-2"
                    />
                    <span className="text-sm text-gray-700">Start Now</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="startTime"
                      value="hour"
                      checked={startTime === "hour"}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="w-4 h-4 text-[#6D49FE] bg-gray-100 border-gray-300 focus:ring-[#6D49FE] focus:ring-2"
                    />
                    <span className="text-sm text-gray-700">Current Hour</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Repeat Interval
                </label>
                <select
                  value={repeatInterval}
                  onChange={(e) => setRepeatInterval(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6D49FE] focus:border-transparent"
                >
                  <option value="1h">Every 1 hour</option>
                  <option value="3h">Every 3 hours</option>
                  <option value="6h">Every 6 hours</option>
                  <option value="12h">Every 12 hours</option>
                  <option value="1d">Every 1 day</option>
                  <option value="3d">Every 3 days</option>
                  <option value="1w">Every 1 week</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={
                  editingSchedule ? handleUpdateSchedule : handleCreateSchedule
                }
                disabled={scheduleLoading}
                className="flex-1 px-4 py-3 bg-[#44087D] text-white rounded-lg hover:bg-[#5a0a94] disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-sm hover:shadow-md"
              >
                {scheduleLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {editingSchedule ? "Updating..." : "Creating..."}
                  </div>
                ) : editingSchedule ? (
                  "Update Schedule"
                ) : (
                  "Create Schedule"
                )}
              </button>
              <button
                onClick={closeScheduleModal}
                disabled={scheduleLoading}
                className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Schedule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the schedule for{" "}
              <span className="font-semibold">
                {scheduleToDelete?.repositoryName}
              </span>
              ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteSchedule}
              className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-500"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default RepositoryScheduleTable;
